import { Box, Typography, useMediaQuery, useTheme, Link } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Description, Lock, BarChart, Folder } from '@mui/icons-material';
import LoginForm from '../forms/LoginForm';

const features = [
  { icon: <Description sx={{ fontSize: 15, color: '#94bce3' }} />, label: 'Gestión documental inteligente' },
  { icon: <Lock sx={{ fontSize: 15, color: '#94bce3' }} />, label: 'Control de acceso por roles' },
  { icon: <BarChart sx={{ fontSize: 15, color: '#94bce3' }} />, label: 'Diagnósticos y reportes en tiempo real' },
];

const LoginPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100vw',
        minHeight: '100vh',
        overflow: 'hidden',
      }}
    >
      {/* ── Left panel ── */}
      {!isMobile && (
        <Box
          sx={{
            width: '38%',
            flexShrink: 0,
            position: 'relative',
            bgcolor: '#1d2d3d',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            p: 5,
            overflow: 'hidden',
          }}
        >
          {/* Brand */}
          <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }} onClick={() => navigate('/')}>
            <Box
              component="img"
              src="/Vertical_Logo.png"
              alt="Simplia"
              sx={{ height: 48, objectFit: 'contain', filter: 'brightness(0) invert(1)', opacity: 0.9 }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </Box>

          {/* Center content */}
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography
              sx={{
                color: '#ffffff',
                fontWeight: 700,
                fontSize: '1.6rem',
                letterSpacing: '-0.02em',
                lineHeight: 1.25,
                mb: 1.5,
              }}
            >
              Tu documentación,<br />ordenada y segura.
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.875rem', lineHeight: 1.65, mb: 4 }}>
              Centraliza, controla y accede a todos tus documentos empresariales desde cualquier lugar.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {features.map((f) => (
                <Box
                  key={f.label}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 2,
                    py: 1.25,
                    borderRadius: 2,
                    bgcolor: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.09)',
                  }}
                >
                  {f.icon}
                  <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.8375rem', fontWeight: 500 }}>
                    {f.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Quote */}
          <Box sx={{ position: 'relative', zIndex: 1, borderLeft: '3px solid rgba(89,128,166,0.5)', pl: 2 }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 300, fontSize: '1rem', lineHeight: 1.55, mb: 0.75, fontStyle: 'italic' }}>
              "Tu información,{' '}
              <Box component="span" sx={{ fontWeight: 700, fontStyle: 'normal', color: '#5980a6' }}>
                tu activo estratégico
              </Box>."
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.07em' }}>
              SIMPLIA v1.0
            </Typography>
          </Box>
        </Box>
      )}

      {/* ── Right panel ── */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#f2f2f3',
          px: { xs: 3, sm: 6, md: 8 },
          py: 5,
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 420 }}>
          {/* Mobile brand */}
          {isMobile && (
            <Box sx={{ mb: 5, cursor: 'pointer' }} onClick={() => navigate('/')}>
              <Typography sx={{ fontWeight: 800, fontSize: '1.3rem', color: '#1d2d3d', letterSpacing: '-0.02em' }}>
                simplia
              </Typography>
            </Box>
          )}

          {/* Header */}
          <Box sx={{ mb: 5 }}>
            <Typography
              component="h1"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.75rem', md: '2.125rem' },
                color: '#1d2d3d',
                letterSpacing: '-0.03em',
                lineHeight: 1.15,
                mb: 1,
              }}
            >
              Bienvenido de nuevo
            </Typography>
            <Typography sx={{ color: 'rgba(29,31,32,0.55)', fontSize: '0.9375rem', lineHeight: 1.6 }}>
              Ingresa tus credenciales para acceder al portal.
            </Typography>
          </Box>

          {/* Form */}
          <LoginForm />

          {/* External Portal Link */}
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Link
              href="/external/portal"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                color: '#5980a6',
                fontSize: '0.875rem',
                fontWeight: 600,
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              <Folder sx={{ fontSize: 18 }} />
              Acceder al Portal de Expedientes
            </Link>
          </Box>

          {/* Trust badges */}
          <Box
            sx={{
              mt: 5,
              pt: 4,
              borderTop: '1px solid rgba(29,31,32,0.16)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 3,
            }}
          >
            {[
              { dot: '#10B981', label: 'SSL SECURE' },
              { dot: '#5980a6', label: '2FA ENABLED' },
            ].map(({ dot, label }) => (
              <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.875 }}>
                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: dot, boxShadow: `0 0 0 3px ${dot}22` }} />
                <Typography sx={{ color: '#98989b', fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.07em' }}>
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>

          <Typography sx={{ textAlign: 'center', mt: 2.5, color: '#CBD5E1', fontSize: '0.75rem' }}>
            © 2026 Simplia · Gestión Documental Inteligente
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default LoginPage;
